import { useState, useEffect } from 'react';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  metaTitle: string;
  metaDesc: string;
  coverImage: string | null;
  authorName: string;
  tags: string[];
  publishedAt: string;
  updatedAt: string;
}

interface BlogPostPageProps {
  slug: string;
  onNavigate: (page: string, data?: any) => void;
  updateTitle: (title: string) => void;
}

const blogContentStyles = `
  .blog-content {
    color: #d1d5db;
    font-size: 1.0625rem;
    line-height: 1.85;
    font-family: 'Georgia', 'Times New Roman', serif;
  }
  .blog-content p {
    margin-bottom: 1.5rem;
    color: #d1d5db;
  }
  .blog-content h2 {
    font-size: 1.6rem;
    font-weight: 700;
    color: #ffffff;
    margin-top: 3rem;
    margin-bottom: 1rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid #374151;
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.3;
  }
  .blog-content h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #f3f4f6;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.4;
  }
  .blog-content h4 {
    font-size: 1.05rem;
    font-weight: 600;
    color: #e5e7eb;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    font-family: 'Inter', system-ui, sans-serif;
  }
  .blog-content ul {
    margin-bottom: 1.5rem;
    padding-left: 0;
    list-style: none;
  }
  .blog-content ul li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.6rem;
    color: #d1d5db;
  }
  .blog-content ul li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.65rem;
    width: 6px;
    height: 6px;
    background-color: #f59e0b;
    border-radius: 50%;
  }
  .blog-content ol {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    counter-reset: list-counter;
    list-style: none;
  }
  .blog-content ol li {
    position: relative;
    padding-left: 1rem;
    margin-bottom: 0.75rem;
    color: #d1d5db;
    counter-increment: list-counter;
  }
  .blog-content ol li::before {
    content: counter(list-counter) '.';
    position: absolute;
    left: -1.5rem;
    color: #f59e0b;
    font-weight: 700;
    font-family: 'Inter', system-ui, sans-serif;
  }
  .blog-content strong {
    color: #fbbf24;
    font-weight: 600;
  }
  .blog-content em {
    color: #e5e7eb;
    font-style: italic;
  }
  .blog-content a {
    color: #fbbf24;
    text-decoration: none;
    border-bottom: 1px solid rgba(251, 191, 36, 0.3);
    transition: border-color 0.2s, color 0.2s;
  }
  .blog-content a:hover {
    color: #fcd34d;
    border-bottom-color: #fcd34d;
  }
  .blog-content blockquote {
    border-left: 3px solid #f59e0b;
    padding: 0.75rem 1.25rem;
    margin: 2rem 0;
    background: rgba(245, 158, 11, 0.05);
    border-radius: 0 0.5rem 0.5rem 0;
    color: #9ca3af;
    font-style: italic;
  }
  .blog-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 2rem 0;
    font-size: 0.9rem;
    font-family: 'Inter', system-ui, sans-serif;
    overflow-x: auto;
    display: block;
  }
  .blog-content thead {
    background: rgba(245, 158, 11, 0.1);
  }
  .blog-content th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-weight: 600;
    color: #fbbf24;
    border-bottom: 1px solid #374151;
    white-space: nowrap;
  }
  .blog-content td {
    padding: 0.7rem 1rem;
    color: #d1d5db;
    border-bottom: 1px solid #1f2937;
    vertical-align: top;
  }
  .blog-content tr:last-child td {
    border-bottom: none;
  }
  .blog-content tr:hover td {
    background: rgba(255,255,255,0.02);
  }
  .blog-content code {
    background: #1f2937;
    color: #fbbf24;
    padding: 0.15rem 0.4rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    font-family: 'Courier New', monospace;
  }
  .blog-content pre {
    background: #111827;
    border: 1px solid #374151;
    border-radius: 0.5rem;
    padding: 1.25rem;
    overflow-x: auto;
    margin: 1.5rem 0;
  }
  .blog-content pre code {
    background: none;
    padding: 0;
    color: #e5e7eb;
  }
  .blog-content hr {
    border: none;
    border-top: 1px solid #374151;
    margin: 2.5rem 0;
  }
`;

export default function BlogPostPage({ slug, onNavigate, updateTitle }: BlogPostPageProps) {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`/api/blog/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => {
        setPost(data);
        updateTitle(data.metaTitle);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  // Inject JSON-LD structured data
  useEffect(() => {
    if (!post) return;

    const existingScript = document.getElementById('blog-schema');
    if (existingScript) existingScript.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDesc,
      author: {
        '@type': 'Organization',
        name: post.authorName,
        url: 'https://scribehaven.com',
      },
      publisher: {
        '@type': 'Organization',
        name: 'ScribeHaven',
        url: 'https://scribehaven.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://scribehaven.com/favicon-32x32.png',
        },
      },
      datePublished: post.publishedAt,
      dateModified: post.updatedAt,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://scribehaven.com/blog/${post.slug}`,
      },
      ...(post.coverImage && {
        image: { '@type': 'ImageObject', url: post.coverImage },
      }),
      keywords: post.tags.join(', '),
      articleSection: 'Writing Resources',
      inLanguage: 'en-US',
    };

    const script = document.createElement('script');
    script.id = 'blog-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('blog-schema');
      if (el) el.remove();
    };
  }, [post]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const estimateReadTime = (content: string) => {
    const words = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
    return Math.max(1, Math.round(words / 200));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"></div>
          <p className="text-gray-500 text-sm">Loading article...</p>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-950">
        <h1 className="text-2xl font-bold text-white mb-4">Article Not Found</h1>
        <p className="text-gray-400 mb-6">This blog post doesn't exist or has been removed.</p>
        <button
          onClick={() => onNavigate('blog')}
          className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold rounded-lg transition-colors"
        >
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <>
      <style>{blogContentStyles}</style>
      <div className="min-h-screen bg-gray-950">

        {/* Hero section with gradient */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-950 border-b border-gray-800/50">
          <div className="max-w-3xl mx-auto px-4 pt-10 pb-12">

            {/* Breadcrumb */}
            <nav className="mb-6 text-sm text-gray-500" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 flex-wrap" itemScope itemType="https://schema.org/BreadcrumbList">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <button
                    onClick={() => onNavigate('home')}
                    className="hover:text-amber-400 transition-colors"
                    itemProp="item"
                  >
                    <span itemProp="name">Home</span>
                  </button>
                  <meta itemProp="position" content="1" />
                </li>
                <li className="text-gray-700">/</li>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <button
                    onClick={() => onNavigate('blog')}
                    className="hover:text-amber-400 transition-colors"
                    itemProp="item"
                  >
                    <span itemProp="name">Blog</span>
                  </button>
                  <meta itemProp="position" content="2" />
                </li>
                <li className="text-gray-700">/</li>
                <li className="text-gray-400 truncate max-w-xs" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <span itemProp="name">{post.title}</span>
                  <meta itemProp="position" content="3" />
                </li>
              </ol>
            </nav>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5">
                {post.tags.slice(0, 4).map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-semibold bg-amber-500/10 text-amber-400 rounded-full border border-amber-500/20 uppercase tracking-wide"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-lg text-gray-400 leading-relaxed mb-6" style={{ fontFamily: "'Georgia', serif" }}>
              {post.excerpt}
            </p>

            {/* Meta row */}
            <div className="flex items-center gap-4 text-sm text-gray-500 border-t border-gray-800 pt-5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <span className="text-amber-400 text-xs font-bold">
                    {post.authorName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-gray-400 font-medium">{post.authorName}</span>
              </div>
              <span className="text-gray-700">·</span>
              <time dateTime={post.publishedAt} className="text-gray-500">
                {formatDate(post.publishedAt)}
              </time>
              <span className="text-gray-700">·</span>
              <span className="text-gray-500">{estimateReadTime(post.content)} min read</span>
            </div>
          </div>
        </div>

        {/* Cover image */}
        {post.coverImage && (
          <div className="max-w-4xl mx-auto px-4 -mt-2 mb-0">
            <div className="rounded-xl overflow-hidden shadow-2xl border border-gray-800">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-auto max-h-96 object-cover"
              />
            </div>
          </div>
        )}

        {/* Article body */}
        <article className="max-w-3xl mx-auto px-4 py-12">
          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* All tags */}
          {post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-800">
              <p className="text-xs text-gray-600 uppercase tracking-widest mb-3 font-semibold">Tagged</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium bg-gray-800 text-gray-400 rounded-full border border-gray-700 hover:border-amber-500/40 hover:text-amber-400 transition-colors cursor-default"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CTA box */}
          <div className="mt-12 rounded-2xl overflow-hidden border border-amber-500/20" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(234,88,12,0.06) 100%)' }}>
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
                <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                Ready to share your story?
              </h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto text-sm leading-relaxed">
                ScribeHaven is the free serial platform built for romance and romantasy authors. No paywalls, no algorithms burying your work. Every story gets discovered.
              </p>
              <button
                onClick={() => onNavigate('register')}
                className="inline-flex items-center gap-2 px-7 py-3 bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-amber-500/20"
              >
                Start Writing Free
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Back to blog */}
          <div className="mt-10 pt-8 border-t border-gray-800">
            <button
              onClick={() => onNavigate('blog')}
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to all articles
            </button>
          </div>
        </article>
      </div>
    </>
  );
}
